import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { CalendarIcon, ChevronUp, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import ContactsField, { ATHLETE_CONTACT_TYPES } from "@/components/ContactsField";
import MultiSelect from "@/components/MultiSelect";
import PhotoUpload from "@/components/PhotoUpload";
import TravelRadiusMap from "@/components/TravelRadiusMap";
import { toast } from "sonner";

const POSITIONS = ["PG", "SG", "SF", "PF", "C"];

// Migrate the legacy fixed `socials` object into the array of {type,value} rows
// used by the shared ContactsField (same widget as Add Team / Add Event).
function socialsToContacts(socials) {
    if (!socials) return [];
    const out = [];
    for (const k of ["facebook", "instagram", "x", "other"]) {
        const v = (socials[k] || "").trim();
        if (v) out.push({ type: k, value: v });
    }
    return out;
}

// Slider goes 50 → 1000 mi smoothly in 10-mile steps, then one final "notch"
// at MAX_TICK (1010) that displays as "Unlimited" and stores the sentinel 9999.
const MIN_MILES = 50;
const MAX_MILES = 1000;    // last numeric tick
const MAX_TICK = 1010;     // slider position that means "Unlimited"
const UNLIMITED = 9999;    // stored DB value when Unlimited is selected

// Height dropdown: 4'0" to 7'5" (48 to 89 inches, inclusive)
const HEIGHT_MIN_IN = 48;
const HEIGHT_MAX_IN = 89;
const HEIGHT_OPTIONS = Array.from({ length: HEIGHT_MAX_IN - HEIGHT_MIN_IN + 1 }, (_, i) => {
    const inches = HEIGHT_MIN_IN + i;
    return { value: String(inches), label: `${Math.floor(inches / 12)}'${inches % 12}"` };
});

// Split a legacy single "name" string into first/last for initial hydration.
function splitLegacyName(full) {
    const parts = (full || "").trim().split(/\s+/);
    if (!parts.length) return { first: "", last: "" };
    if (parts.length === 1) return { first: parts[0], last: "" };
    return { first: parts[0], last: parts.slice(1).join(" ") };
}

// Athlete age is displayed to teams as of a fixed reference date (not "today"),
// so a roster's age brackets stay stable across a season.
const AGE_AS_OF = new Date(2027, 7, 1); // Aug 01, 2027
const AGE_AS_OF_LABEL = "Aug 01, 2027";
const MIN_AGE = 13;

function ageFromISO(iso, asOf = new Date()) {
    if (!iso) return null;
    try {
        const d = parseISO(iso);
        if (Number.isNaN(d.getTime())) return null;
        let age = asOf.getFullYear() - d.getFullYear();
        const m = asOf.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && asOf.getDate() < d.getDate())) age--;
        return age;
    } catch {
        return null;
    }
}

/**
 * PlayerProfileForm — the full player/athlete profile editor: name, birthdate,
 * position, height, home ZIP + max-travel slider with live radius map, bio, socials.
 * Shared by the standalone /profile page and the player Account Management panel.
 */
export default function PlayerProfileForm({ expanded: expandedProp, onExpandedChange, hideAddButton = false }) {
    const { user, loading, updatePlayerProfile, deletePlayerProfile } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [visSaving, setVisSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Section starts collapsed to just the purple title bar; the pill expands it.
    // Optionally controlled by a parent (e.g. the STANDARD box "+ Create" button).
    const [expandedInternal, setExpandedInternal] = useState(false);
    const expanded = expandedProp !== undefined ? expandedProp : expandedInternal;
    const setExpanded = (v) => {
        const nv = typeof v === "function" ? v(expanded) : v;
        if (onExpandedChange) onExpandedChange(nv);
        else setExpandedInternal(nv);
    };
    // "Created yet?" — birthdate is required to save, so it's the reliable signal.
    const hasProfile = !!user?.birthdate;

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/login"); return; }
        const legacy = splitLegacyName(user.name);
        const acctFirst = user.first_name || legacy.first || "";
        const acctLast = user.last_name || legacy.last || "";
        const acctMi = user.middle_initial || "";
        const rel = user.athlete_relation || "athlete";
        // The athlete's name is separate from the account/username name. When the
        // account owner IS the athlete, it mirrors the account name (locked); a
        // parent/guardian can set a different athlete name.
        const athFirst = rel === "athlete" ? acctFirst : (user.athlete_first_name ?? acctFirst);
        const athLast = rel === "athlete" ? acctLast : (user.athlete_last_name ?? acctLast);
        const athMi = rel === "athlete" ? acctMi : (user.athlete_middle_initial ?? acctMi);
        setForm({
            first_name: athFirst,
            last_name: athLast,
            middle_initial: athMi,
            birthdate: user.birthdate || "",
            grad_year: user.grad_year || 2028,
            position: user.position || "PG",
            height_inches: user.height_inches || 72,
            home_zip: user.home_zip || "",
            max_travel_distance: user.max_travel_distance ?? 300,
            athlete_relation: rel,
            school: user.school || "",
            bio: user.bio || "",
            photo: user.photo || "",
            athlete_photo: user.athlete_photo || "",
            visible_to_teams: user.visible_to_teams ?? user.open_to_offers ?? true,
            visible_to_scouts: user.visible_to_scouts ?? user.open_to_offers ?? true,
            contacts: (user.contacts && user.contacts.length) ? user.contacts : socialsToContacts(user.socials),
        });
    }, [user, loading, navigate]);

    const derivedAge = useMemo(() => ageFromISO(form?.birthdate, AGE_AS_OF), [form?.birthdate]);
    const currentAge = useMemo(() => ageFromISO(form?.birthdate), [form?.birthdate]);
    const underage = currentAge != null && currentAge < MIN_AGE;

    // Account/username identity name — the athlete name mirrors this (locked) when
    // the account owner is the athlete themselves.
    const account = useMemo(() => {
        const legacy = splitLegacyName(user?.name);
        return {
            first: user?.first_name || legacy.first || "",
            last: user?.last_name || legacy.last || "",
            mi: user?.middle_initial || "",
        };
    }, [user]);
    const isAthleteSelf = form?.athlete_relation !== "guardian";
    const setRelation = (val) => {
        if (val === "athlete") {
            // Mirror the account name onto the athlete profile and lock it.
            setForm((f) => ({ ...f, athlete_relation: val, first_name: account.first, last_name: account.last, middle_initial: account.mi }));
        } else {
            setForm((f) => ({ ...f, athlete_relation: val }));
        }
    };

    // Grad-year options: limited to 8 years from the current year. On/after Sept 1,
    // drop the current year (those athletes have graduated) so it starts at next year.
    const gradYears = useMemo(() => {
        const now = new Date();
        const y = now.getFullYear();
        let years = Array.from({ length: 9 }, (_, i) => y + i); // current year + next 8
        if (now.getMonth() >= 8) years = years.filter((yr) => yr !== y);
        return years;
    }, []);

    if (!form) return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.first_name.trim() || !form.last_name.trim()) {
            toast.error("First and last name are required");
            return;
        }
        if (!form.birthdate) {
            toast.error("Please select your birthdate");
            return;
        }
        if (underage) {
            toast.error(`Minimum age to create an athlete profile is ${MIN_AGE}`);
            return;
        }
        setSaving(true);
        const res = await updatePlayerProfile({
            athlete_first_name: form.first_name.trim(),
            athlete_last_name: form.last_name.trim(),
            athlete_middle_initial: (form.middle_initial || "").trim().slice(0, 1).toUpperCase(),
            birthdate: form.birthdate,
            grad_year: Number(form.grad_year),
            position: form.position,
            height_inches: Number(form.height_inches),
            home_zip: form.home_zip,
            max_travel_distance: Number(form.max_travel_distance),
            athlete_relation: form.athlete_relation || "athlete",
            school: (form.school || "").trim(),
            bio: form.bio,
            photo: form.photo,
            athlete_photo: form.athlete_photo,
            visible_to_teams: form.visible_to_teams,
            visible_to_scouts: form.visible_to_scouts,
            contacts: (form.contacts || []).filter((c) => (c.value || "").trim()),
        });
        setSaving(false);
        if (res.ok) toast.success("Profile saved");
        else toast.error(res.error);
    };

    const handleVisibility = async (v) => {
        const prevVt = form.visible_to_teams;
        const prevVs = form.visible_to_scouts;
        setForm({ ...form, visible_to_teams: v, visible_to_scouts: v });
        if (!hasProfile) return; // no saved profile yet — value persists locally until created
        setVisSaving(true);
        const res = await updatePlayerProfile({ visible_to_teams: v, visible_to_scouts: v });
        setVisSaving(false);
        if (res.ok) {
            toast.success(v ? "Athlete profile is now visible" : "Athlete profile hidden");
        } else {
            setForm((f) => ({ ...f, visible_to_teams: prevVt, visible_to_scouts: prevVs }));
            toast.error(res.error || "Could not update visibility");
        }
    };

    const doDelete = async () => {
        setDeleting(true);
        const res = await deletePlayerProfile();
        setDeleting(false);
        setConfirmDelete(false);
        if (res.ok) {
            toast.success("Athlete profile deleted");
            setExpanded(false);
        } else {
            toast.error(res.error || "Could not delete profile");
        }
    };

    const birthdateDate = form.birthdate ? parseISO(form.birthdate) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-2.5" data-testid="player-profile-form">
            {/* Collapsible bar: just the "Athlete Profile" title in a purple border when
                collapsed. The pill reads ADD (nothing saved yet) or EDIT (already created)
                and expands the full form. */}
            <div className="border border-[#a855f7] bg-[#0f0f0d] flex items-center justify-between gap-3 px-4 py-3.5" data-testid="athlete-profile-bar">
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#c084fc] flex items-center gap-2">
                        {hasProfile ? (
                            <Link to={`/players/${user.id}`} data-testid="athlete-profile-link" className="hover:underline underline-offset-2">Athlete profile</Link>
                        ) : (
                            <span>Athlete profile</span>
                        )}
                    </div>
                    <div data-testid="athlete-visibility">
                        <VisibilityRow
                            label="Visible"
                            testidYes="visible-pro-yes"
                            testidNo="visible-pro-no"
                            value={form.visible_to_teams}
                            disabled={visSaving}
                            onChange={handleVisibility}
                        />
                    </div>
                </div>
                {(hasProfile || expanded || !hideAddButton) && (
                <button
                    type="button"
                    data-testid="athlete-profile-toggle"
                    onClick={() => setExpanded((v) => !v)}
                    className="inline-flex items-center gap-2 border border-[#a855f7] text-[#c084fc] hover:bg-[#a855f7]/10 px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors rounded-none"
                >
                    {expanded
                        ? (<><ChevronUp className="h-3.5 w-3.5" /> Collapse</>)
                        : (<><Plus className="h-3.5 w-3.5" /> {hasProfile ? "Edit" : "Add"}</>)}
                </button>
                )}
            </div>

            {expanded && (
              <div className="space-y-2.5 pt-1" data-testid="athlete-profile-body">
                <div data-testid="athlete-relation-select" className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">I am the</Label>
                    <div className="flex flex-wrap gap-2">
                        {[["athlete", "Athlete"], ["guardian", "Parent/Guardian"]].map(([val, lbl]) => (
                            <button
                                key={val}
                                type="button"
                                data-testid={`athlete-relation-${val}`}
                                onClick={() => setRelation(val)}
                                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${form.athlete_relation === val ? "border-[#a855f7] bg-[#a855f7]/15 text-white" : "border-[#2a2a2a] text-[#a0a0a0] hover:border-[#a855f7]/50"}`}
                            >
                                {lbl}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex flex-wrap gap-3 items-start">
                <div className="border border-[#2a2a2a] p-3 bg-[#141414] w-[300px] shrink-0">
                <PhotoUpload value={form.athlete_photo} onChange={(v) => setForm((f) => ({ ...f, athlete_photo: v }))} portrait label="Athlete headshot" hint="Portrait crop (like a phone photo) · shown to Pro members for your athlete profile." dragPaste testid="player-headshot" />
            </div>

            {/* Identity + basics — to the right of the headshot, wrapping to fill available width. */}
            <div className="flex-1 min-w-[18rem] flex flex-wrap gap-x-2 gap-y-2.5 items-start">
                <div className="flex-1 min-w-[9rem]">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">First Name <span className="text-[#ff4500]">*</span></Label>
                    <Input data-testid="profile-first-name" value={form.first_name} onChange={set("first_name")} disabled={isAthleteSelf} className="mt-1.5 h-10 rounded-none bg-[#141414] border-[#2a2a2a] disabled:opacity-60 disabled:cursor-not-allowed" required />
                </div>
                <div className="w-14">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">M.I.</Label>
                    <Input data-testid="profile-middle-initial" value={form.middle_initial} onChange={(e) => setForm({ ...form, middle_initial: e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 1).toUpperCase() })} disabled={isAthleteSelf} maxLength={1} placeholder="—" className="mt-1.5 h-10 rounded-none bg-[#141414] border-[#2a2a2a] text-center px-0 disabled:opacity-60 disabled:cursor-not-allowed" />
                </div>
                <div className="flex-1 min-w-[9rem]">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Last Name <span className="text-[#ff4500]">*</span></Label>
                    <Input data-testid="profile-last-name" value={form.last_name} onChange={set("last_name")} disabled={isAthleteSelf} className="mt-1.5 h-10 rounded-none bg-[#141414] border-[#2a2a2a] disabled:opacity-60 disabled:cursor-not-allowed" required />
                </div>
                <div className="w-32">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Birthdate <span className="text-[#ff4500]">*</span></Label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <button type="button" data-testid="profile-birthdate-trigger" className={cn("mt-1.5 h-10 w-full flex items-center justify-between px-2 rounded-none bg-[#141414] border border-[#2a2a2a] text-left text-sm", !form.birthdate && "text-[#6a6a6a]")}>
                                <span data-testid="profile-birthdate-value">{birthdateDate ? format(birthdateDate, "MMM d, yyyy") : "Select"}</span>
                                <CalendarIcon className="h-4 w-4 text-[#a0a0a0] shrink-0" />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#141414] border-[#2a2a2a] rounded-none" align="start">
                            <Calendar mode="single" selected={birthdateDate} onSelect={(d) => { if (!d) return; setForm({ ...form, birthdate: format(d, "yyyy-MM-dd") }); }} captionLayout="dropdown-buttons" fromYear={new Date().getFullYear() - 30} toYear={new Date().getFullYear() - MIN_AGE} defaultMonth={birthdateDate || new Date(new Date().getFullYear() - 16, 0, 1)} disabled={(d) => d > new Date()} initialFocus />
                        </PopoverContent>
                    </Popover>
                    <p className="mt-1.5 text-[10px] text-[#a0a0a0] leading-tight" data-testid="profile-birthdate-note">
                        Only current and age as of 01AUG27 will be shown.
                    </p>
                    {underage && (
                        <p className="mt-1 text-[10px] text-[#ff4500] leading-tight" data-testid="profile-underage-note">
                            Minimum age to create an athlete profile is {MIN_AGE}.
                        </p>
                    )}
                </div>
                <div className="w-24">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Grad Yr <span className="text-[#ff4500]">*</span></Label>
                    <Select value={form.grad_year ? String(form.grad_year) : undefined} onValueChange={(v) => setForm({ ...form, grad_year: v })}>
                        <SelectTrigger data-testid="profile-grad" className="mt-1.5 h-10 rounded-none bg-[#141414] border-[#2a2a2a] px-2 gap-1"><SelectValue placeholder="Year" /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none max-h-64 min-w-0 w-[var(--radix-select-trigger-width)]">
                            {gradYears.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-24">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Position <span className="text-[#ff4500]">*</span></Label>
                    <div className="mt-1.5">
                        <MultiSelect testid="profile-position" options={POSITIONS.map((p) => [p, p])} value={(form.position || "").split(",").map((s) => s.trim()).filter(Boolean)} onChange={(arr) => setForm({ ...form, position: arr.join(", ") })} label="Positions" placeholder="Select" hideSelectAll compact neutral />
                    </div>
                </div>
                <div className="w-20">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Height <span className="text-[#ff4500]">*</span></Label>
                    <Select value={String(form.height_inches)} onValueChange={(v) => setForm({ ...form, height_inches: Number(v) })}>
                        <SelectTrigger data-testid="profile-height" className="mt-1.5 h-10 rounded-none bg-[#141414] border-[#2a2a2a] px-2 gap-1"><SelectValue placeholder="Height" /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none max-h-64 min-w-0 w-[var(--radix-select-trigger-width)]">
                            {HEIGHT_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="shrink-0 w-40">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">School</Label>
                    <Input data-testid="profile-school" value={form.school} onChange={set("school")} placeholder="School name" maxLength={120} className="mt-1.5 h-10 rounded-none bg-[#141414] border-[#2a2a2a] placeholder:text-[#666]" />
                </div>
                <div className="w-20">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] whitespace-nowrap">ZIP Code <span className="text-[#ff4500]">*</span></Label>
                    <Input data-testid="profile-zip" value={form.home_zip} onChange={set("home_zip")} placeholder="Enter zip" pattern="[0-9]{5}" maxLength={5} className="mt-1.5 h-10 rounded-none bg-[#141414] border-[#2a2a2a] px-2 placeholder:text-[#666]" />
                </div>
            </div>
            </div>

            {/* Max Travel — full width so the slider has room */}
            <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] flex items-center justify-between">
                    <span>Max Travel <span className="normal-case tracking-normal text-[#a0a0a0]">(location of team practices)</span></span>
                    <span data-testid="profile-maxtravel-label" className="text-[#a855f7] normal-case tracking-normal">
                        {form.max_travel_distance >= UNLIMITED ? "Unlimited" : `${form.max_travel_distance} mi`}
                    </span>
                </Label>
                <div className="mt-3 flex items-center gap-3">
                    <span className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0] shrink-0">{MIN_MILES}</span>
                    <Slider
                        data-testid="profile-maxtravel-slider"
                        min={MIN_MILES}
                        max={MAX_TICK}
                        step={10}
                        value={[
                            form.max_travel_distance >= UNLIMITED
                                ? MAX_TICK
                                : Math.min(Math.max(form.max_travel_distance || MIN_MILES, MIN_MILES), MAX_MILES),
                        ]}
                        onValueChange={([v]) => setForm({ ...form, max_travel_distance: v >= MAX_TICK ? UNLIMITED : v })}
                        trackClassName="h-2 bg-[#0f0f0f] border border-[#2a2a2a]"
                        rangeClassName="bg-[#a855f7]"
                        thumbClassName="h-5 w-5 border-2 border-[#a855f7] bg-white shadow-md"
                        className="flex-1"
                    />
                    <span className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0] shrink-0">Unltd</span>
                </div>
            </div>
            <TravelRadiusMap
                zip={form.home_zip}
                radiusMiles={form.max_travel_distance >= UNLIMITED ? null : form.max_travel_distance}
                height="170px"
                color="#a855f7"
            />

            <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Bio</Label>
                <Textarea data-testid="profile-bio" value={form.bio} onChange={set("bio")} rows={3} className="mt-2 rounded-none bg-[#141414] border-[#2a2a2a]" />
            </div>

            <ContactsField
                value={form.contacts || []}
                onChange={(next) => setForm({ ...form, contacts: next })}
                testidPrefix="profile-contact"
                accent="#a855f7"
                types={ATHLETE_CONTACT_TYPES}
            />

            <div className="flex flex-wrap gap-2.5 justify-end">
                <Button type="submit" disabled={saving} data-testid="profile-save" className="h-12 px-8 rounded-none bg-[#a855f7] hover:bg-[#9333ea] uppercase tracking-[0.2em] text-xs font-semibold">
                    {saving ? "Saving…" : "Save"}
                </Button>
                {hasProfile && (
                    <Button type="button" onClick={() => setConfirmDelete(true)} disabled={saving || deleting} data-testid="profile-delete" className="h-12 px-5 rounded-none bg-transparent border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white uppercase tracking-[0.2em] text-xs font-semibold inline-flex items-center gap-2">
                        <Trash2 className="h-4 w-4" /> Delete
                    </Button>
                )}
            </div>

            {confirmDelete && (
                <div className="fixed inset-0 z-[2000] bg-black/80 flex items-center justify-center p-4" data-testid="profile-delete-dialog" onClick={(e) => { if (e.target === e.currentTarget && !deleting) setConfirmDelete(false); }}>
                    <div className="w-full max-w-md bg-[#141414] border border-[#2a2a2a] p-6">
                        <div className="flex items-center gap-2 text-[#ef4444]">
                            <Trash2 className="h-5 w-5" />
                            <h3 className="font-display text-xl tracking-wide">Delete athlete profile?</h3>
                        </div>
                        <p className="text-sm text-[#cfcfcf] mt-3">
                            This will permanently delete all athlete profile information — birthdate, grad year, position, measurements, bio, contacts, headshot, travel radius, and visibility settings. This cannot be undone.
                        </p>
                        <div className="flex gap-2 mt-6">
                            <Button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting} data-testid="profile-delete-cancel" className="flex-1 h-11 rounded-none bg-[#0f0f0f] border border-[#2a2a2a] hover:border-white uppercase tracking-[0.15em] text-xs">Cancel</Button>
                            <Button type="button" onClick={doDelete} disabled={deleting} data-testid="profile-delete-confirm" className="flex-1 h-11 rounded-none bg-[#ef4444] hover:bg-[#dc2626] text-white uppercase tracking-[0.15em] text-xs font-semibold">
                                {deleting ? "Deleting…" : "Delete permanently"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
              </div>
            )}
        </form>
    );
}

function VisibilityRow({ label, testidYes, testidNo, value, onChange, disabled = false }) {
    return (
        <div className="flex items-center justify-between gap-4">
            <div className="text-sm text-[#e5e5e5]">{label}</div>
            <div className="flex gap-2" role="group" aria-label={label}>
                <button
                    type="button"
                    data-testid={testidYes}
                    aria-pressed={value === true}
                    disabled={disabled}
                    onClick={() => onChange(true)}
                    className={`h-9 px-4 text-[11px] uppercase tracking-[0.2em] font-semibold border transition-colors rounded-none disabled:opacity-50 disabled:cursor-not-allowed ${
                        value === true
                            ? "bg-[#16a34a] border-[#16a34a] text-white"
                            : "bg-transparent border-[#2a2a2a] text-[#a0a0a0] hover:text-white hover:border-[#3a3a3a]"
                    }`}
                >
                    Yes
                </button>
                <button
                    type="button"
                    data-testid={testidNo}
                    aria-pressed={value === false}
                    disabled={disabled}
                    onClick={() => onChange(false)}
                    className={`h-9 px-4 text-[11px] uppercase tracking-[0.2em] font-semibold border transition-colors rounded-none disabled:opacity-50 disabled:cursor-not-allowed ${
                        value === false
                            ? "bg-[#ff4500] border-[#ff4500] text-white"
                            : "bg-transparent border-[#2a2a2a] text-[#a0a0a0] hover:text-white hover:border-[#3a3a3a]"
                    }`}
                >
                    No
                </button>
            </div>
        </div>
    );
}
